<template>
	<v-card
		variant="outlined"
		class="resource-card-shell"
	>
		<template v-if="driveConfig">
			<div class="entity-card">
				<!-- Header: icon + name -->
				<div class="entity-card__header">
					<span
						class="data-source-icon data-source-icon--drive data-source-icon--32"
						role="img"
						aria-label="Data Drive"
					/>
					<div class="sgnl-flex sgnl-align-center">
						<v-tooltip
							bottom
							:allow-overflow="true"
						>
							<template v-slot:activator="{ props }">
								<span
									class="sgnl-paragraph-m-medium sgnl-text-color-default"
									v-bind="props"
									>{{ driveConfig.Name }}</span
								>
							</template>
							<div>Unique Source ID: {{ driveConfig.DriveID }}</div>
						</v-tooltip>
					</div>
				</div>

				<div class="entity-card__body">
					<!-- Badges: mount points -->
					<div class="sgnl-flex sgnl-align-center sgnl-flex-wrap sgnl-gap-8">
						<template v-if="connectedSourceCount > 0">
							<span
								v-if="gatewayCount > 0"
								class="entity-card__badge sgnl-text-color-secondary"
							>
								<v-icon size="small">{{ dataSourceIcon("Gateway") }}</v-icon>
								{{ gatewayCount }} {{ gatewayCount == 1 ? "Gateway" : "Gateways" }}
							</span>
							<span
								v-if="bucketCount > 0"
								class="entity-card__badge sgnl-text-color-secondary"
							>
								<v-icon size="small">{{ dataSourceIcon("Bucket") }}</v-icon>
								{{ bucketCount }} {{ bucketCount == 1 ? "S3 Bucket" : "S3 Buckets" }}
							</span>
							<span
								v-if="signaloidCloudStorageConnected"
								class="entity-card__badge sgnl-text-color-secondary"
							>
								<FolderShared16 class="sgnl-icon-color-secondary" />
								Cloud Storage
							</span>
						</template>
						<span
							v-else
							class="entity-card__badge entity-card__badge--warning"
						>
							No Source Mount Points Set
						</span>
					</div>

					<!-- Metadata -->
					<div class="sgnl-paragraph-s-regular entity-card__meta sgnl-text-color-secondary">
						<div
							v-if="!$vuetify.display.mobile"
							class="sgnl-flex sgnl-align-center sgnl-gap-4"
						>
							<Calendar16 class="sgnl-icon-color-secondary" />Created:
							{{ formatDate(driveConfig.CreatedAt) }}
						</div>
						<div class="sgnl-flex sgnl-align-center sgnl-gap-4">
							<Time16 class="sgnl-icon-color-secondary" />Last Updated:
							{{ formatDate(driveConfig.UpdatedAt) }}
						</div>
					</div>
				</div>

				<!-- Actions -->
				<div class="entity-card__actions">
					<v-btn
						variant="flat"
						color="primary"
						@click="showDriveConfigDialog = true"
					>
						<Settings16 />
						<span>Edit Configuration</span>
					</v-btn>
					<v-spacer />
					<v-btn
						variant="outlined"
						color="error"
						data-test="data-drive-disconnect"
						@click="showDisconnectDialog = true"
					>
						<TrashCan16 />
						<v-tooltip activator="parent">Disconnect</v-tooltip>
					</v-btn>
				</div>
			</div>
		</template>
		<DataSourceCardSkeleton v-else />

		<DriveConfigDialog
			v-if="showDriveConfigDialog"
			v-model:showDialog="showDriveConfigDialog"
			:driveConfigToUpdate="driveConfig"
			@drive-config-updated="driveConfigUpdatedHandler"
		/>
		<DisconnectDriveDialog
			v-if="showDisconnectDialog"
			v-model:showDialog="showDisconnectDialog"
			:driveData="driveConfig"
			@drive-disconnected="driveDisconnectedHandler"
			@close-dialog="showDisconnectDialog = false"
		/>
	</v-card>
</template>

<script>
import { defineComponent } from "vue";
import Settings16 from "@carbon/icons-vue/es/settings/16";
import TrashCan16 from "@carbon/icons-vue/es/trash-can/16";
import Calendar16 from "@carbon/icons-vue/es/calendar/16";
import Time16 from "@carbon/icons-vue/es/time/16";
import FolderShared16 from "@carbon/icons-vue/es/folder--shared/16";
import DriveConfigDialog from "@/components/DataSources/DriveConfigDialog.vue";
import DisconnectDriveDialog from "@/components/DataSources/DisconnectDriveDialog.vue";
import DataSourceCardSkeleton from "@/components/DataSources/DataSourceCardSkeleton.vue";
import { cloneDeep } from "lodash";
import { mapActions } from "pinia";
import { sdk } from "@/js/sdk";
import { useDataSourcesStore } from "@/stores/dataSources";
import * as dsUtil from "@/components/DataSources/utilities";
import { monitoringCaptureError } from "@/plugins/monitoring";

export default defineComponent({
	name: "DriveCard",
	components: {
		DriveConfigDialog,
		DisconnectDriveDialog,
		DataSourceCardSkeleton,
		Settings16,
		TrashCan16,
		Calendar16,
		Time16,
		FolderShared16,
	},
	props: {
		driveID: {
			type: String,
			required: true,
		},
		initialDriveConfig: {
			type: Object,
			default: undefined,
		},
	},
	emits: {
		"drive-disconnected": () => true,
		"drive-disconnection-failed": () => true,
		"drive-config-updated": () => true,
	},
	data: () => ({
		showDriveConfigDialog: false,
		showDisconnectDialog: false,
		driveConfig: undefined,
	}),
	methods: {
		...mapActions(useDataSourcesStore, ["setDriveConfig"]),
		formatDate(dtString) {
			return dsUtil.formatDateDayString(1000 * dtString);
		},
		driveConfigUpdatedHandler() {
			this.getDriveInfo();
			this.$emit("drive-config-updated");
		},
		async getDriveInfo() {
			try {
				const resp = await sdk.drives.getOne(this.driveID);
				if (resp) {
					this.driveConfig = resp;
					this.setDriveConfig(resp);
				} else {
					throw new Error("Bad response");
				}
			} catch (error) {
				monitoringCaptureError(error, "Fetch data drive config");
			}
		},
		driveDisconnectedHandler() {
			this.$emit("drive-disconnected");
		},
		dataSourceIcon(dataSourceType) {
			return dsUtil.dataSourceIcon(dataSourceType);
		},
	},

	computed: {
		gatewayCount() {
			if (!Array.isArray(this.driveConfig.DataSources)) {
				return 0;
			}
			return this.driveConfig.DataSources.filter((source) => {
				return source.ResourceType === "Gateway";
			}).length;
		},
		bucketCount() {
			if (!Array.isArray(this.driveConfig.DataSources)) {
				return 0;
			}
			return this.driveConfig.DataSources.filter((source) => {
				return source.ResourceType === "Bucket";
			}).length;
		},
		signaloidCloudStorageConnected() {
			return this.driveConfig.DataSources.find((source) => {
				return source.ResourceType === "SignaloidCloudStorage";
			});
		},
		connectedSourceCount() {
			return this.driveConfig.DataSources.length;
		},
	},
	mounted() {
		if (!this.initialDriveConfig) {
			this.getDriveInfo();
		} else {
			this.driveConfig = cloneDeep(this.initialDriveConfig);
			this.setDriveConfig(this.driveConfig);
		}
	},
});
</script>
